import AppsButton from '@assets/icons/apps/AppsButton';
import { AuthMeUser } from '@common/types/user';
import { AppsGrid } from '@components/layout/search';
import { Search } from '@components/utility/inputs';
import { APP_PREFIX_PATH } from '@constants/common';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import {
    Avatar,
    Box,
    Button,
    ButtonBase,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Popover,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { NotificationsButton } from '@notifications/components/utility/buttons';
import { stringAvatar } from '@utils/common';
import { pathToVP, toLogin } from '@utils/route';
import { getRoleName } from '@utils/user';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

export type SearchToolbarProps = {
    search?: string;
    onSearchChange: (value: string) => void;
    scopeLabel?: string;
    onSearchFocus?: () => void;
    onScopeDelete?: () => void;
    user?: AuthMeUser;
    userLoading?: boolean;
};

export const SearchToolbar = ({
    search = '',
    onSearchChange,
    scopeLabel,
    onSearchFocus,
    onScopeDelete,
    user,
    userLoading,
}: SearchToolbarProps) => {
    const [appsAnchorEl, setAppsAnchorEl] = useState<null | HTMLElement>(null);
    const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);
    const [accountMenuWidth, setAccountMenuWidth] = useState(220);
    const blurOnKeyUpRef = useRef(false);

    const isMdOrAbove = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });

    const intl = useIntl();

    const handleAccountMenuOpen = (anchorEl: HTMLElement) => {
        if (typeof window !== 'undefined') {
            setAccountMenuWidth(Math.max(window.innerWidth - anchorEl.getBoundingClientRect().left - 16, 220));
        }

        setAccountAnchorEl(anchorEl);
    };

    const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            onScopeDelete?.();
        }
    };

    return (
        <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={{ xs: '8px', lg: '24px' }}
        >
            <Search.Public
                value={search}
                fullWidth
                placeholder={intl.formatMessage({
                    id: 'search.placeholder',
                    defaultMessage: 'What are you looking for?',
                })}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                    if (!scopeLabel || e.key !== 'Backspace') {
                        blurOnKeyUpRef.current = false;
                        return;
                    }

                    const target = e.target;

                    blurOnKeyUpRef.current = target instanceof HTMLInputElement && target.value.length === 0;
                }}
                onKeyUp={(e) => {
                    if (!scopeLabel || e.key !== 'Backspace') {
                        return;
                    }

                    if (blurOnKeyUpRef.current) {
                        onScopeDelete?.();
                        blurOnKeyUpRef.current = false;
                    }
                }}
                scope={{
                    label: scopeLabel,
                    onDelete: onScopeDelete,
                }}
                onClear={onScopeDelete}
                onFocus={onSearchFocus}
                onBlur={handleSearchBlur}
            />
            <IconButton className="hidden md:block p-0" onClick={(e) => setAppsAnchorEl(e.currentTarget)}>
                <AppsButton selected={Boolean(appsAnchorEl)} />
            </IconButton>
            <Popover
                anchorEl={appsAnchorEl}
                open={Boolean(appsAnchorEl) && isMdOrAbove}
                onClose={() => setAppsAnchorEl(null)}
                className="shadow-none translate-y-[6px]"
                elevation={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                classes={{
                    paper: 'rounded-lg border',
                }}
            >
                <AppsGrid />
            </Popover>
            {/* Notifications */}
            {!userLoading && !!user && <NotificationsButton />}
            <Divider orientation="vertical" className="h-[47px]" sx={{ display: { xs: 'none', md: 'block' } }} />
            {user || userLoading ? (
                <Stack
                    direction="row"
                    gap="12px"
                    sx={{ display: { xs: 'none', md: 'flex' } }}
                    alignItems="center"
                    component={ButtonBase}
                    px="6px"
                    py="8px"
                    borderRadius="8px"
                    onClick={(e) => handleAccountMenuOpen(e.currentTarget)}
                    className={clsx('border border-solid border-transparent', {
                        'bg-neutral-100 !border-[rgb(0,0,0,0.16)]': Boolean(accountAnchorEl),
                    })}
                >
                    {!userLoading ? (
                        <Avatar
                            {...stringAvatar(
                                user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : ''
                            )}
                            variant="circular"
                            sx={{
                                width: 46,
                                height: 46,
                            }}
                        />
                    ) : (
                        <Skeleton variant="circular" width={46} height={46} />
                    )}
                    <Box maxWidth={{ md: 100, lg: 300 }} textAlign="left">
                        {!userLoading ? (
                            <Typography
                                fontFamily="museo-sans"
                                fontSize={16}
                                fontWeight={600}
                                letterSpacing={-0.32}
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                            >
                                {user?.first_name && user?.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : intl.formatMessage({ id: 'common.userName', defaultMessage: 'User Name' })}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" width={100} height={16} />
                        )}
                        {!userLoading && user?.role ? (
                            <Typography
                                fontFamily="museo-sans"
                                fontSize={12}
                                color="neutral-900"
                                letterSpacing={-0.24}
                                textTransform="uppercase"
                                fontWeight={300}
                            >
                                {getRoleName(user.role, intl)}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" width={60} height={12} />
                        )}
                    </Box>
                </Stack>
            ) : (
                <Button
                    variant="text"
                    color="neutral-600"
                    className="font-medium text-sm"
                    endIcon={<PersonOutlinedIcon />}
                    component={Link}
                    to={toLogin()}
                >
                    Login
                </Button>
            )}
            <Menu
                anchorEl={accountAnchorEl}
                open={Boolean(accountAnchorEl)}
                onClose={() => setAccountAnchorEl(null)}
                className="mt-1"
                elevation={1}
                slotProps={{
                    paper: {
                        sx: {
                            width: accountMenuWidth,
                            maxWidth: 'none',
                        },
                    },
                }}
            >
                <MenuItem component={Link} to={`${APP_PREFIX_PATH}/accounts`} onClick={() => setAccountAnchorEl(null)}>
                    <Typography color="neutral-800" fontSize={14} letterSpacing={0}>
                        <FormattedMessage id="account.overview" defaultMessage="Account Overview" />
                    </Typography>
                </MenuItem>
                <MenuItem
                    component="a"
                    href={pathToVP('/accounts/logout').toString()}
                    onClick={() => setAccountAnchorEl(null)}
                >
                    <Typography color="neutral-800" fontSize={14} letterSpacing={0}>
                        <FormattedMessage id="account.logout" defaultMessage="Logout" />
                    </Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
};
